import React from 'react';
import { CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { SmartImportRecord } from '../types';
import { useIntl } from 'react-intl';

const phaseListStyles = {
  alignSelf: 'flex-start',
  maxWidth: 760,
  width: '100%',
};
const completeIconStyles = { color: 'success.main' };
const pendingIconStyles = { color: 'text.disabled' };
const skeletonRows = Array.from({ length: 8 }, (_, index) => index);

interface ProcessingPhaseProps {
  active: boolean;
  complete: boolean;
  label: string;
}

const ProcessingPhase = React.memo(
  ({ active, complete, label }: ProcessingPhaseProps) => (
    <Stack direction="row" gap={1.5} alignItems="center">
      {complete ? (
        <CheckCircleOutlineIcon sx={completeIconStyles} />
      ) : active ? (
        <CircularProgress size={24} />
      ) : (
        <RadioButtonUncheckedIcon sx={pendingIconStyles} />
      )}
      <Typography color={active || complete ? 'text.primary' : 'text.disabled'}>
        {label}
      </Typography>
    </Stack>
  )
);

interface ImportProcessingProps {
  smartImport: SmartImportRecord;
}

export const ImportProcessing = React.memo(
  ({ smartImport }: ImportProcessingProps) => {
    const intl = useIntl();
    const profileComplete = smartImport.progress.totalChunks > 0;
    const rowsComplete =
      smartImport.progress.processedRowCount >=
      smartImport.progress.totalRowCount;

    return (
      <Stack gap={3} flexGrow={1} minHeight={0}>
        <Stack gap={3} sx={phaseListStyles}>
          <ProcessingPhase
            active={!profileComplete}
            complete={profileComplete}
            label={intl.formatMessage({
              id: 'imports.processing.profile',
              defaultMessage: 'Preparing the semantic profile',
            })}
          />
          <ProcessingPhase
            active={profileComplete && !rowsComplete}
            complete={rowsComplete}
            label={intl.formatMessage(
              {
                id: 'imports.processing.rows',
                defaultMessage:
                  'Interpreting find rows ({processed} / {total})',
              },
              {
                processed: smartImport.progress.processedRowCount,
                total: smartImport.progress.totalRowCount,
              }
            )}
          />
        </Stack>
        <Stack
          gap={1}
          aria-label={intl.formatMessage({
            id: 'imports.processing.loadingRows',
            defaultMessage: 'Loading import rows',
          })}
        >
          {skeletonRows.map((row) => (
            <Skeleton height={44} key={row} variant="rounded" />
          ))}
        </Stack>
      </Stack>
    );
  }
);
